import { useParams } from 'react-router-dom';
import { match, P } from 'ts-pattern';
import AddressDisplay from '~/components/AddressDisplay';
import Alert from '~/components/Alert';
import Badge from '~/components/Badge';
import Card from '~/components/Card';
import Form from '~/components/Form';
import Icon from '~/components/Icon';
import LinkButton from '~/components/LinkButton';
import Submit from '~/components/Submit';
import { useIsLoading } from '~/utils/routing';
import { useActionResult, useLoaderData } from './orderEditCommit';

type LoaderData = ReturnType<typeof useLoaderData>;

function AddressConfirm({
  data,
}: {
  data: Extract<LoaderData, { editType: 'UPDATE_SHIPPING_ADDRESS' }>;
}) {
  const isLoading = useIsLoading();
  const {
    currentAddress,
    stagedEdits: { updatedShippingAddress },
    action: { editConfirmLabel },
  } = data;

  return (
    <Form>
      <div className="flex flex-row items-center justify-around gap-4">
        <div className="flex flex-col gap-2">
          <Badge>Previous Address</Badge>
          {currentAddress ?
            <AddressDisplay address={currentAddress} />
          : <p>Address not provided</p>}
        </div>

        <Icon icon="ArrowRight" className="shrink-0" />

        <div className="flex flex-col gap-2">
          <Badge>New Address</Badge>
          <AddressDisplay address={updatedShippingAddress} />
        </div>
      </div>

      {editConfirmLabel && <p className="mb-4">{editConfirmLabel}</p>}
      <Submit loading={isLoading}>Update Address</Submit>
    </Form>
  );
}

function CancelConfirm({
  data,
}: {
  data: Extract<LoaderData, { editType: 'CANCEL_ORDER' }>;
}) {
  const isLoading = useIsLoading();
  const {
    action: { editConfirmLabel },
  } = data;

  return (
    <Form>
      {editConfirmLabel && <p className="mb-4">{editConfirmLabel}</p>}
      <Submit loading={isLoading}> Cancel Order</Submit>
    </Form>
  );
}

function ResultDisplay({
  title,
  variant,
  message,
}: {
  title: string;
  variant: 'success' | 'warning';
  message: string;
}) {
  const params = useParams();

  return (
    <>
      <Alert title={title} variant={variant}>
        {message}
      </Alert>
      <LinkButton
        relative="path"
        to={`/${params.store}/order/${params.idFromPlatform}`}
        className="w-full shrink-0 md:w-60"
      >
        Go Home
      </LinkButton>
    </>
  );
}

export default function OrderEditCommitPage() {
  const result = useActionResult();
  const loaderData = useLoaderData();

  const content = match(loaderData)
    .with({ editType: 'UPDATE_SHIPPING_ADDRESS' }, (data) => (
      <AddressConfirm data={data} />
    ))
    .with({ editType: 'CANCEL_ORDER' }, (data) => <CancelConfirm data={data} />)
    .exhaustive();

  return (
    <Card headline={loaderData.headline} hideHeader>
      {match(result)
        .with({ ok: true, message: P.select() }, (msg) => (
          <ResultDisplay title="Success" variant="success" message={msg} />
        ))
        .with({ ok: false, message: P.select() }, (msg) => (
          <ResultDisplay
            title="Something went wrong"
            variant="warning"
            message={msg}
          />
        ))
        .with(P.nullish, () => content)
        .exhaustive()}
    </Card>
  );
}
